import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Box, Button } from '@cloudscape-design/components';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component for split panel content
 * Catches errors in child components and displays a user-friendly error message
 */
export class SplitPanelErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Split panel error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box padding="l">
          <Alert
            type="error"
            header="Unable to load content"
            action={
              <Button onClick={this.handleReset} variant="primary">
                Try Again
              </Button>
            }
          >
            An error occurred while loading the panel content. Please try again or contact
            support if the problem persists.
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}
